/**
 * GuideSidebar — left pane of the App Guide. Groups sections by group id
 * and highlights the active section. The MUI 5 `List` API keeps things
 * simple; the sidebar collapses to a drawer on mobile, handled by the
 * parent page.
 */
import React from 'react';
import { Box, List, ListItemButton, ListItemIcon, ListItemText, Typography, Chip } from '@mui/material';
import { SECTIONS, GROUPS } from './sections';
import { useAppTheme } from '../../hooks/useAppTheme';

interface Props {
  activeId: string;
  onSelect: (id: string) => void;
}

const GuideSidebar: React.FC<Props> = ({ activeId, onSelect }) => {
  const { C, isDark } = useAppTheme();

  return (
    <Box
      sx={{
        width: { xs: '100%', md: 260 },
        flexShrink: 0,
        borderRight: { md: '1px solid' },
        borderColor: 'divider',
        bgcolor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.015)',
        height: { md: 'calc(100vh - 110px)' },
        overflowY: 'auto',
        position: { md: 'sticky' },
        top: { md: 56 },
        py: 1,
      }}
    >
      {GROUPS.map((group) => {
        const items = SECTIONS.filter(s => s.group === group.id);
        if (items.length === 0) return null;
        return (
          <Box key={group.id} sx={{ mb: 1 }}>
            <Typography
              sx={{
                px: 2, pt: 1.25, pb: 0.5,
                fontSize: '0.66rem', fontWeight: 700,
                textTransform: 'uppercase', letterSpacing: 0.6,
                color: 'text.secondary',
              }}
            >
              {group.label}
            </Typography>
            <List dense disablePadding>
              {items.map((s) => {
                const isActive = s.id === activeId;
                return (
                  <ListItemButton
                    key={s.id}
                    selected={isActive}
                    onClick={() => onSelect(s.id)}
                    sx={{
                      py: 0.5,
                      pl: 2.25, pr: 1.5,
                      borderLeft: '3px solid',
                      borderLeftColor: isActive ? C.accent : 'transparent',
                      bgcolor: isActive ? (isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)') : 'transparent',
                      '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)' },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 28, color: isActive ? C.accent : 'text.secondary' }}>
                      {s.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={s.label}
                      primaryTypographyProps={{
                        fontSize: '0.82rem',
                        fontWeight: isActive ? 700 : 500,
                        color: isActive ? 'text.primary' : 'text.secondary',
                      }}
                    />
                    {s.draft && (
                      <Chip
                        label="draft"
                        size="small"
                        sx={{
                          height: 16, fontSize: '0.55rem', fontWeight: 700,
                          bgcolor: C.warnMuted, color: C.warn,
                          ml: 0.5,
                        }}
                      />
                    )}
                  </ListItemButton>
                );
              })}
            </List>
          </Box>
        );
      })}
    </Box>
  );
};

export default GuideSidebar;
